// @ts-check
import { Calendar, Mail, RefreshCw, Send, Sparkles, User } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import LoadingScreen from '../components/LoadingScreen';
import BulkActionBar from '../components/ui/BulkActionBar';
import { HeaderCheckbox, RowCheckbox } from '../components/ui/RowCheckbox';
import { useToast } from '../context/ToastContext';
import useRowSelection from '../hooks/useRowSelection';
import { reportBulkResult, runBulk } from '../services/bulkService';
import api from '../utils/apiClient';

const ContactSubmissionsPage = () => {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [generatingReply, setGeneratingReply] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [bulkBusy, setBulkBusy] = useState(false);
  const selection = useRowSelection(
    submissions.map((s) => s.id),
    { resetKey: filterStatus },
  );

  const loadSubmissions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const url =
        filterStatus === 'all'
          ? '/api/admin/contact-submissions'
          : `/api/admin/contact-submissions?status=${filterStatus}`;

      const data = await api.get(url);
      setSubmissions(data);
    } catch (error) {
      console.error('Failed to load submissions:', error);
      setError('Failed to load contact submissions. Please try again.');
      toast.error('Failed to load submissions');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, toast]);

  useEffect(() => {
    loadSubmissions();
  }, [loadSubmissions]);

  const updateStatus = async (submissionId, newStatus) => {
    try {
      const updated = await api.patch(`/api/admin/contact-submissions/${submissionId}`, { status: newStatus });

      toast.success('Status updated');
      await loadSubmissions();
      if (selectedSubmission?.id === submissionId) {
        setSelectedSubmission(updated);
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const bulkSetStatus = async (status, verb) => {
    setBulkBusy(true);
    try {
      const result = await runBulk('/api/admin/contact-submissions/bulk', {
        ids: selection.selectedIds,
        action: 'set_status',
        params: { status },
      });
      reportBulkResult(toast, result, verb, 'submission');
      selection.clear();
      await loadSubmissions();
    } catch (error) {
      toast.error(error.message || 'Failed to update submissions');
    } finally {
      setBulkBusy(false);
    }
  };

  const sendReply = async () => {
    if (!replyText.trim()) return;

    try {
      setSending(true);
      await api.post(`/api/admin/contact-submissions/${selectedSubmission.id}/reply`, { message: replyText });

      toast.success('Reply sent');
      setReplyText('');
      // Reload submission to get updated thread
      const updated = await api.get(`/api/admin/contact-submissions/${selectedSubmission.id}`);
      setSelectedSubmission(updated);
      await loadSubmissions();
    } catch (error) {
      toast.error('Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const generateReply = async () => {
    if (!selectedSubmission) return;
    if (replyText.trim() && !window.confirm('This will replace the current draft. Continue?')) return;

    setGeneratingReply(true);
    try {
      const result = await api.post(`/api/admin/contact-submissions/${selectedSubmission.id}/generate-reply`);
      setReplyText(result.reply || '');
    } catch (error) {
      toast.error(error.message || 'Failed to generate reply');
    } finally {
      setGeneratingReply(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      new: 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300',
      in_progress: 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-300',
      replied: 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300',
      resolved: 'bg-muted text-foreground',
      archived: 'bg-muted text-muted-foreground',
    };
    return colors[status] || 'bg-muted text-foreground';
  };

  if (loading && submissions.length === 0) {
    return <LoadingScreen />;
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
        <button
          onClick={loadSubmissions}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground hover:bg-primary/90"
        >
          <RefreshCw size={18} />
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-[350px,1fr] h-[calc(100vh-120px)] gap-6">
      {/* Left: Submissions List */}
      <div className="flex flex-col">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h1 className="text-2xl font-serif">Contact Submissions</h1>
          <button
            onClick={loadSubmissions}
            disabled={loading}
            className="p-2 border rounded-lg border-input hover:bg-muted/50"
            title="Refresh"
          >
            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>

        {/* Filter */}
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="mb-4 px-3 py-2 border border-input rounded-lg focus:outline-none focus:border-foreground"
        >
          <option value="all">All Submissions</option>
          <option value="new">New</option>
          <option value="in_progress">In Progress</option>
          <option value="replied">Replied</option>
          <option value="resolved">Resolved</option>
          <option value="archived">Archived</option>
        </select>

        <BulkActionBar count={selection.count} onClear={selection.clear}>
          <BulkActionBar.Button onClick={() => bulkSetStatus('in_progress', 'marked in progress')} disabled={bulkBusy}>
            In progress
          </BulkActionBar.Button>
          <BulkActionBar.Button onClick={() => bulkSetStatus('resolved', 'resolved')} disabled={bulkBusy}>
            Resolve
          </BulkActionBar.Button>
          <BulkActionBar.Button onClick={() => bulkSetStatus('archived', 'archived')} disabled={bulkBusy}>
            Archive
          </BulkActionBar.Button>
        </BulkActionBar>
        {submissions.length > 0 && (
          <label className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
            <HeaderCheckbox selection={selection} label="Select all submissions" />
            Select all
          </label>
        )}

        {/* List */}
        <div className="flex-1 overflow-y-auto border border-border rounded-lg">
          {submissions.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">No submissions found</div>
          ) : (
            submissions.map((submission) => (
              <div
                key={submission.id}
                onClick={() => setSelectedSubmission(submission)}
                className={`p-4 border-b border-border cursor-pointer hover:bg-muted/50 ${
                  selectedSubmission?.id === submission.id ? 'bg-blue-50 dark:bg-blue-950/50' : ''
                }`}
              >
                <div className="flex justify-between items-start mb-2">
                  <div className="flex items-center gap-2">
                    {/* Own click target so ticking the box doesn't open the submission */}
                    <span onClick={(e) => e.stopPropagation()}>
                      <RowCheckbox selection={selection} id={submission.id} label={`Select ${submission.name}`} />
                    </span>
                    <div className="font-medium text-sm">{submission.name}</div>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-md ${getStatusColor(submission.status)}`}>
                    {submission.status}
                  </span>
                </div>
                <div className="text-xs text-muted-foreground mb-1">{submission.email}</div>
                {submission.subject && <div className="text-sm text-foreground/80 mb-1">{submission.subject}</div>}
                <div className="text-xs text-muted-foreground">
                  {new Date(submission.created_at).toLocaleDateString()}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Right: Submission Detail & Thread */}
      <div className="flex flex-col border border-border rounded-lg">
        {!selectedSubmission ? (
          <div className="flex items-center justify-center h-full text-muted-foreground">
            Select a submission to view details
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="border-b border-border p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h2 className="text-xl font-semibold mb-1">{selectedSubmission.subject || 'Contact Inquiry'}</h2>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <User size={14} /> {selectedSubmission.name}
                    </span>
                    <span className="flex items-center gap-1">
                      <Mail size={14} /> {selectedSubmission.email}
                    </span>
                    {selectedSubmission.phone && <span>{selectedSubmission.phone}</span>}
                  </div>
                </div>
                <select
                  value={selectedSubmission.status}
                  onChange={(e) => updateStatus(selectedSubmission.id, e.target.value)}
                  className="px-3 py-1 border border-input rounded-lg text-sm"
                >
                  <option value="new">New</option>
                  <option value="in_progress">In Progress</option>
                  <option value="replied">Replied</option>
                  <option value="resolved">Resolved</option>
                  <option value="archived">Archived</option>
                </select>
              </div>
            </div>

            {/* Email Thread */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-muted/50">
              {/* Original Message */}
              <div className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2 text-sm text-muted-foreground">
                  <User size={16} />
                  <span className="font-medium">{selectedSubmission.name}</span>
                  <span>•</span>
                  <Calendar size={14} />
                  <span>{new Date(selectedSubmission.created_at).toLocaleString()}</span>
                </div>
                <p className="text-foreground whitespace-pre-wrap">{selectedSubmission.message}</p>
              </div>

              {/* Thread Replies */}
              {selectedSubmission.email_thread?.map((entry) => (
                <div
                  key={entry.id}
                  className={`border rounded-lg p-4 ${
                    entry.is_from_admin
                      ? 'bg-blue-50 dark:bg-blue-950/50 border-blue-200 dark:border-blue-900 ml-8'
                      : 'bg-card border-border mr-8'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2 text-sm">
                    {entry.is_from_admin ? (
                      <span className="font-medium text-blue-700 dark:text-blue-300">Admin: {entry.sender_name}</span>
                    ) : (
                      <span className="font-medium">{entry.sender_name}</span>
                    )}
                    <span>•</span>
                    <span className="text-muted-foreground">{new Date(entry.created_at).toLocaleString()}</span>
                  </div>
                  <p className="text-foreground whitespace-pre-wrap">{entry.message}</p>
                </div>
              ))}
            </div>

            {/* Reply Box */}
            <div className="border-t border-border p-4 bg-card">
              <textarea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder="Type your reply here..."
                className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:border-foreground resize-none"
                rows={3}
              />
              <div className="flex justify-between items-center mt-2">
                <button
                  onClick={generateReply}
                  disabled={generatingReply}
                  className="flex items-center gap-2 px-3 py-2 text-sm border border-input rounded-lg hover:bg-muted/50 disabled:opacity-50"
                  title="Draft a reply from the customer's message and thread so far"
                >
                  <Sparkles size={16} className={generatingReply ? 'animate-pulse' : ''} />
                  {generatingReply ? 'Drafting...' : 'Draft with AI'}
                </button>
                <button
                  onClick={sendReply}
                  disabled={sending || !replyText.trim()}
                  className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                >
                  <Send size={18} />
                  {sending ? 'Sending...' : 'Send Reply'}
                </button>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Note: This will be recorded in the email thread. Actual email sending requires integration with email
                service.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ContactSubmissionsPage;
